import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectSettings } from "../redux/slices/homepageSlice";
import { useTranslation } from "../hooks/useTranslation";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";

const FloatingButtons = () => {
  const settings = useSelector(selectSettings);
  const { t } = useTranslation();
  const { toggleLanguage, isArabic } = useLanguage();
  const { isLightMode, toggleTheme } = useTheme();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);

  // Show buttons after scrolling down a bit
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setIsVisible(scrollTop > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Only show on homepage and public pages, not on dashboard
  const isDashboardPage = location.pathname.startsWith("/dashboard");

  const handlePhoneClick = () => {
    if (settings?.contact?.phone) {
      window.open(`tel:${settings.contact.phone}`, "_self");
    }
  };

  const handleWhatsAppClick = () => {
    if (settings?.contact?.whatsapp) {
      // Check if it's already a WhatsApp URL
      if (
        settings.contact.whatsapp.startsWith("https://wa.me/") ||
        settings.contact.whatsapp.startsWith("whatsapp://")
      ) {
        window.open(settings.contact.whatsapp, "_blank", "noopener,noreferrer");
      } else {
        // If it's a regular URL, try to convert it to WhatsApp format
        // Extract phone number from URL if possible
        let phoneNumber = settings.contact.whatsapp;

        // Remove common URL parts
        phoneNumber = phoneNumber.replace(
          /^https?:\/\/(wa\.me\/|api\.whatsapp\.com\/send\?phone=)/,
          ""
        );
        phoneNumber = phoneNumber.replace(/[^\d+]/g, ""); // Keep only digits and +

        // Try WhatsApp app protocol first (for mobile)
        const whatsappAppUrl = `whatsapp://send?phone=${phoneNumber}`;
        const whatsappWebUrl = `https://wa.me/${phoneNumber}`;

        // Try to open WhatsApp app first
        const appWindow = window.open(
          whatsappAppUrl,
          "_blank",
          "noopener,noreferrer"
        );

        // If app doesn't open (fallback to web), try web version after a short delay
        setTimeout(() => {
          if (!appWindow || appWindow.closed) {
            window.open(whatsappWebUrl, "_blank", "noopener,noreferrer");
          }
        }, 1000);
      }
    }
  };

  const handleThemeToggle = () => {
    toggleTheme();
  };

  if (isDashboardPage) {
    return null;
  }

  return (
    <div className={`floating-buttons ${isVisible ? "visible" : ""}`}>
      {/* Theme Toggle Button - Always visible above contact button */}
      <button
        className="floating-btn theme-btn"
        onClick={handleThemeToggle}
        title={isLightMode ? "Switch to Dark Mode" : "Switch to Light Mode"}
        aria-label={
          isLightMode ? "Switch to Dark Mode" : "Switch to Light Mode"
        }
      >
        {isLightMode ? (
          // Moon icon for switching to dark mode
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="20px"
            height="20px"
          >
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        ) : (
          // Sun icon for switching to light mode
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="20px"
            height="20px"
          >
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
        )}
      </button>

      {/* Language Toggle Button */}
      <button
        className="floating-btn language-btn "
        onClick={toggleLanguage}
        title={isArabic ? "Switch to English" : "Switch to Arabic"}
        aria-label={isArabic ? "Switch to English" : "Switch to Arabic"}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          width="20px"
          height="20px"
        >
          <path
            d="M20.58 19.37L17.59 11.01C17.38 10.46 16.91 10.12 16.37 10.12C15.83 10.12 15.37 10.46 15.14 11.03L12.16 19.37C12.02 19.76 12.22 20.19 12.61 20.33C13 20.47 13.43 20.27 13.57 19.88L14.19 18.15H18.54L19.16 19.88C19.27 20.19 19.56 20.38 19.87 20.38C19.95 20.38 20.04 20.37 20.12 20.34C20.51 20.2 20.71 19.77 20.57 19.38L20.58 19.37ZM14.74 16.64L16.38 12.05L18.02 16.64H14.74ZM12.19 7.85C9.92999 11.42 7.89 13.58 5.41 15.02C5.29 15.09 5.16 15.12 5.04 15.12C4.78 15.12 4.53 14.99 4.39 14.75C4.18 14.39 4.3 13.93 4.66 13.73C6.75999 12.51 8.48 10.76 10.41 7.86H4.12C3.71 7.86 3.37 7.52 3.37 7.11C3.37 6.7 3.71 6.36 4.12 6.36H7.87V4.38C7.87 3.97 8.21 3.63 8.62 3.63C9.02999 3.63 9.37 3.97 9.37 4.38V6.36H13.12C13.53 6.36 13.87 6.7 13.87 7.11C13.87 7.52 13.53 7.86 13.12 7.86H12.18L12.19 7.85ZM12.23 15.12C12.1 15.12 11.97 15.09 11.85 15.02C11.2 14.64 10.57 14.22 9.97999 13.78C9.64999 13.53 9.58 13.06 9.83 12.73C10.08 12.4 10.55 12.33 10.88 12.58C11.42 12.99 12.01 13.37 12.61 13.72C12.97 13.93 13.09 14.39 12.88 14.75C12.74 14.99 12.49 15.12 12.23 15.12Z"
            fill="#000000"
          ></path>
        </svg>
      </button>

      {/* Phone Button - Always visible */}
      {settings?.contact?.phone && (
        <button
          className="floating-btn phone-btn"
          onClick={handlePhoneClick}
          title="Call Us"
          aria-label="Call Us"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="20px"
            height="20px"
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
          </svg>
        </button>
      )}

      {/* WhatsApp Button - Always visible */}
      {settings?.contact?.whatsapp && (
        <button
          className="floating-btn whatsapp-btn"
          onClick={handleWhatsAppClick}
          title="WhatsApp"
          aria-label="Contact us on WhatsApp"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            width="20px"
            height="20px"
          >
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g
              id="SVGRepo_tracerCarrier"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></g>
            <g id="SVGRepo_iconCarrier">
              <path
                d="M6.014 8.00613C6.12827 7.1024 7.30277 5.87414 8.23488 6.01043L8.23339 6.00894C9.14051 6.18132 9.85859 7.74261 10.2635 8.44465C10.5504 8.95402 10.3641 9.4701 10.0965 9.68787C9.7355 9.97883 9.17099 10.3803 9.28943 10.7834C9.5 11.5 12 14 13.2296 14.7107C13.695 14.9797 14.0325 14.2702 14.3207 13.9067C14.5301 13.6271 15.0466 13.46 15.5548 13.736C16.3138 14.178 17.0288 14.6917 17.69 15.27C18.0202 15.546 18.0977 15.9539 17.8689 16.385C17.4659 17.1443 16.3003 18.1456 15.4542 17.9421C13.9764 17.5868 8 15.27 6.08033 8.55801C5.97237 8.24048 5.99955 8.12044 6.014 8.00613Z"
                fill="currentColor"
              ></path>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 23C10.7764 23 10.0994 22.8687 9 22.5L6.89443 23.5528C5.56462 24.2177 4 23.2507 4 21.7639V19.5C1.84655 17.492 1 15.1767 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23ZM6 18.6303L5.36395 18.0372C3.69087 16.4772 3 14.7331 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C11.0143 21 10.552 20.911 9.63595 20.6038L8.84847 20.3397L6 21.7639V18.6303Z"
                fill="currentColor"
              ></path>
            </g>
          </svg>
        </button>
      )}
    </div>
  );
};

export default FloatingButtons;
